export interface Palette {
  primary: string; light: string; shadow: string; dark: string;
  accent: string; accentLight: string; accentDark: string;
  glass: string; glassDark: string; glassLight: string;
  exhaust: string; exhaustHot: string; exhaustCool: string;
  metal: string; metalDark: string; metalLight: string;
  smoke: string;
  detail: string; detailLight: string; detailDark: string;
  trim: string; trimLight: string; trimDark: string;
}

export interface PaletteSats {
  primary?: number; // 0–100, default 100
  accent?: number;
  glow?: number;
  detail?: number;
  trim?: number;
}

export const hslToHex = (h: number, s: number, l: number): string => {
  s /= 100; l /= 100;
  const k = (n: number) => (n + h / 30) % 12;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, 9 - k(n), 1));
    return Math.round(255 * c).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

export const buildPalette = (hue: number, accentHue: number, glowHue?: number, detailHue?: number, trimHue?: number, sats?: PaletteSats): Palette => {
  const gw = glowHue ?? (accentHue + 180) % 360;
  const dh = detailHue ?? (hue + 200) % 360;
  const th = trimHue ?? 210;
  const ps = (sats?.primary ?? 100) / 100;
  const as_ = (sats?.accent ?? 100) / 100;
  const gs = (sats?.glow ?? 100) / 100;
  const ds = (sats?.detail ?? 100) / 100;
  const ts = (sats?.trim ?? 100) / 100;
  return ({
  primary: hslToHex(hue, 55 * ps, 55),
  light: hslToHex(hue, 50 * ps, 72),
  shadow: hslToHex(hue, 60 * ps, 32),
  dark: hslToHex(hue, 65 * ps, 20),
  accent: hslToHex(accentHue, 70 * as_, 55),
  accentLight: hslToHex(accentHue, 75 * as_, 72),
  accentDark: hslToHex(accentHue, 75 * as_, 35),
  glass: hslToHex(gw, 60 * gs, 70),
  glassDark: hslToHex(gw, 65 * gs, 40),
  glassLight: hslToHex(gw, 60 * gs, 85),
  exhaust: hslToHex(dh, 85 * ds, 70),
  exhaustHot: hslToHex(dh, 60 * ds, 88),
  exhaustCool: hslToHex((dh - 20 + 360) % 360, 90 * ds, 55),
  metal: hslToHex(th, 12 * ts, 65),
  metalDark: hslToHex(th, 18 * ts, 42),
  metalLight: hslToHex(th, 8 * ts, 78),
  smoke: hslToHex(th, 8 * ts, 50),
  detail: hslToHex(dh, 60 * ds, 50),
  detailLight: hslToHex(dh, 65 * ds, 68),
  detailDark: hslToHex(dh, 65 * ds, 30),
  trim: hslToHex(th, 30 * ts, 55),
  trimLight: hslToHex(th, 25 * ts, 72),
  trimDark: hslToHex(th, 35 * ts, 32),
});
};
